import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useState, useCallback } from "react";

import {
  createProfile,
  getCurrentProfile,
  clearMessage,
} from "@/services/userServices/profileAction";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import NotificationAlertOptions from "@/layouts/Alerts";
import isEmpty from "@/validation/is-empty";
import ProfileView from "./ProfileView"; 
import { asCaught } from "@/services/caughtError";

/** What ImageUploadContainer hands back: a picked file, or a media-library pick. */
type PickedImage = {
  data?: string;
  imgName?: string;
  secure_url?: string;
  context?: unknown;
};

/** The stored avatar URL, or the image just picked in the form. */
type Avatar = string | PickedImage | null;

const ProfileContainer = () => {
  //state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [avatar, setAvatar] = useState<Avatar>("");
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  // redux
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();
  const profileState = useAppSelector((s) => s.profile);
  const globalErrors = useAppSelector((s) => s.errors);
  // SCRUM-1024: raised when the profile fetch failed — the form is then showing
  // blanks that are NOT the stored values.
  const loadError = !!profileState?.loadError;
  // load current profile
  useEffect(() => {
    dispatch(getCurrentProfile());
  }, [dispatch]);
 
  useEffect(() => {
    if (globalErrors) setErrors(globalErrors);
  }, [globalErrors]);

  // SCRUM-1024: the load failure is otherwise invisible — ProfileView only
  // renders field-level errors, so a fetch failure would leave a blank-looking
  // form with no explanation.
  useEffect(() => {
    if (loadError) {
      NotificationAlertOptions(
        "error",
        "Loading your profile failed. Please reload the page."
      );
    }
  }, [loadError]);
  //when profile fetched the first time -->hydrate form
  useEffect(() => {
    const p = profileState?.profile;
    if (p && isEmpty(name)) {
      setName(!isEmpty(p.name) ? p.name : "");
      setEmail(!isEmpty(p.email) ? p.email : "");
      setRole(!isEmpty(p.role) ? p.role : "");
      setAvatar(!isEmpty(p.avatar) ? p.avatar : "");
    }
  }, [profileState?.profile, name]);

  useEffect(() => {
    if (profileState?.message) {
      NotificationAlertOptions("success", profileState.message);
      dispatch(clearMessage());
      setLoading(false);
    }
  }, [profileState?.message, dispatch]);
  // handlers
  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: field, value } = e.target;
    if (field === "name") setName(value);
    if (field === "email") setEmail(value);
    if (field === "role") setRole(value);
  }, []);

  const onImagePicked = useCallback((img: PickedImage | null) => {
  
    setAvatar(img);
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // SCRUM-1024: the disabled Edit button is not enough — a form still
      // submits via implicit submission (Enter in a text input). The load
      // failed, so these fields are blanks, not the stored profile; saving
      // would overwrite the real record.
      if (loadError) return;

      let payload: { name: string; email: string; role: string; avatar?: Avatar } = { name, email, role };

      // Freshly picked file: upload first, then save inline with the returned URL
      // (useImageUpload clears images.url on success). The secure_url and existing-
      // avatar branches below are unchanged.
      if (avatar && typeof avatar !== "string" && avatar.data) {
        const path = `/USERS/${avatar.imgName}`;
        setLoading(true);
        try {
          const url = await uploadImage(avatar.data, path);
          dispatch(createProfile({ name, email, role, avatar: url }));
        } catch (err) {
          setLoading(false);
          NotificationAlertOptions(
            "error",
            asCaught(err)?.response?.data?.message ||
              asCaught(err)?.message ||
              "Saving the profile failed. Please try again."
          );
        }
        return;
      }

      // If we already have a secure_url on avatar object
      if (avatar && typeof avatar !== "string" && avatar.secure_url) {
        payload = { ...payload, avatar: avatar.secure_url };
      } else if (avatar) {
        // string or anything else
        payload = { ...payload, avatar };
      }

      dispatch(createProfile(payload));
    },
    [name, email, role, avatar, dispatch, uploadImage, loadError]
  );

  return (
    <ProfileView
      name={name}
      email={email}
      role={role}
      avatar={avatar}
      errors={errors}
      loading={loading}
      onChange={onChange}
      onSubmit={onSubmit}
      onImagePicked={onImagePicked}
      // SCRUM-1024: the form never loaded, so its fields are empty for a reason
      // that has nothing to do with the stored profile. Saving now would PUT
      // those blanks over the real record.
      saveDisabled={loadError}
    />
  );
};

export default ProfileContainer;
